<template>
  <g ref="singlebar">
    <g
      v-for="(bar, index) in bars"
      :key="index"
    >
      <g v-if="hasTransitions">
        <!-- Do not bind y, height and fill to avoid
        conflict between Vue and D3 controlling DOM -->
        <rect
          class="single-bar"
          :width="bar.width"
          :x="bar.x"
          :opacity="barOpacity(index)"
          @mouseover="onMouseover($event, bar, index)"
          @mouseleave="onMouseleave()"
        ></rect>
      </g>
      <g v-else>
        <rect
          class="single-bar"
          :fill="primaryColor"
          :height="bar.height"
          :width="bar.width"
          :x="bar.x"
          :y="bar.y"
          :opacity="barOpacity(index)"
          @mouseover="onMouseover($event, bar, index)"
          @mouseleave="onMouseleave()"
        ></rect>
      </g>
    </g>
  </g>
</template>

<script>
import { bandScale, linearScale }
  from '../../../../../../../services/dashboard/graphs/scalesService';
import { select } from 'd3';

export default {
  props: {
    // From Graph Wrapper
    data: {
      type: Array,
      required: true,
    },
    defaultChartMax: {
      type: Number,
    },
    primaryKey: {
      type: String,
      required: true,
    },
    dimensionKey: {
      type: String,
      required: true,
    },
    contentDimensions: {
      type: Object,
      required: true,
    },
    marginTop: {
      type: Number,
      default: 0,
    },
    // Custom to Single Bar
    customChartMax: {
      type: Number,
    },
    barWidth: {
      type: Number,
      default: 24,
    },
    barPadding: {
      type: Number,
      default: 0,
    },
    primaryColor: {
      type: String,
      default: '#4369F4',
    },
    hasTransitions: {
      type: Boolean,
      default: false,
    },
    hoveredIndex: {
      type: Number,
      default: null,
    },
  },
  computed: {
    chartMax() {
      return this.customChartMax || this.defaultChartMax;
    },
    xScale() {
      const range = { min: 0, max: this.contentDimensions.width };
      const args = [ this.data, this.dimensionKey, range ];

      return bandScale(...args);

    },
    yScale() {
      const range = { min: this.contentDimensions.height, max: this.marginTop };
      const domain = { min: 0, max: this.chartMax };

      return linearScale(range, domain);
    },
    bars() {
      return this.data.map(d => {
        return {
          d,
          width: this.barWidth,
          height: this.contentDimensions.height - this.yScale(d[this.primaryKey]),
          x: this.xScale(d[this.dimensionKey]),
          y: this.yScale(d[this.primaryKey]),
        };
      });
    },
    hasOnHover() {
      return this.$listeners && this.$listeners.onHover;
    },
  },
  methods: {
    onMouseleave() {
      this.$emit('onLeave');
    },
    onMouseover(event, bar, index) {
      if (this.hasOnHover) {
        this.$emit('onHover', {
          data: {
            d: bar.d,
          },
          hoveredIndex: index,
          position: {
            left: bar.x + 2*(this.barWidth),
            top: event.offsetY,
          },
        });
      }
    },
    barOpacity(index) {
      if (this.hoveredIndex === null) return 1;

      return this.hoveredIndex === index ? 1 : .25;
    },
    mountTransitions() {
      const barNode = this.$refs.singlebar;

      return select(barNode)
        .selectAll('rect')
        .data(this.bars)
      // Set initial height to 0 to have bars grow
      // from bottom of chart instead of top
        .attr('fill', _ => { return this.primaryColor; })
        .attr('y', _ => { return this.yScale(0); })
        .attr('height', _ => { return 0; })
        .transition()
        .duration(750)
        .attr('y',  d => { return d.y; })
        .attr('height', d => { return d.height; })
        .delay(function(d, i) {
          return i * 150;
        });
    },
    updateTransitions() {
      const barNode = this.$refs.singlebar;

      select(barNode)
        .selectAll('.single-bar')
        .data(this.bars)
        .transition()
        .duration(500)
        .attr('y',  d => { return d.y; })
        .attr('height', d => { return d.height; })
        .attr('fill', _ => { return this.primaryColor; });
    },
  },
  mounted() {
    if (this.hasTransitions) {
      this.mountTransitions();
    }
  },
  updated() {
    if (this.hasTransitions) {
      this.updateTransitions();
    }
  },
};
</script>

